import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jobs } from '../data/jobs';
import { industries } from '../data/industries';

export type JobFilters = { q: string; location: string; industry: string; seniority: string; type: string };
export type JobSort = 'featured' | 'newest';

const PAGE_SIZE = 6;
const KEYS: (keyof JobFilters)[] = ['q', 'location', 'industry', 'seniority', 'type'];

export function useJobSearch() {
  const [params, setParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [sort, setSort] = useState<JobSort>('featured');

  const filters: JobFilters = {
    q: params.get('q') ?? '',
    location: params.get('location') ?? '',
    industry: params.get('industry') ?? '',
    seniority: params.get('seniority') ?? '',
    type: params.get('type') ?? '',
  };

  const setFilter = (key: keyof JobFilters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setVisible(PAGE_SIZE);
  };

  const clearAll = () => {
    const next = new URLSearchParams(params);
    KEYS.forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    setVisible(PAGE_SIZE);
  };

  const results = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const loc = filters.location.trim().toLowerCase();
    const filtered = jobs.filter((job) => {
      const industryName = industries.find((i) => i.slug === job.industry)?.name ?? '';
      const haystack = [job.title, job.summary, job.overview, industryName, ...job.competencies, ...job.qualifications].join(' ').toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (loc && !`${job.location} ${job.arrangement}`.toLowerCase().includes(loc)) return false;
      if (filters.industry && job.industry !== filters.industry) return false;
      if (filters.seniority && job.seniority !== filters.seniority) return false;
      if (filters.type && job.type !== filters.type) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sort === 'featured' && Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
      return b.posted.localeCompare(a.posted);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.location, filters.industry, filters.seniority, filters.type, sort]);

  const activeCount = KEYS.filter((k) => filters[k]).length;

  return {
    filters,
    setFilter,
    clearAll,
    sort,
    setSort,
    results,
    shown: results.slice(0, visible),
    hasMore: visible < results.length,
    loadMore: () => setVisible((v) => v + PAGE_SIZE),
    activeCount,
  };
}
